import { Fragment } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    AtlSiteType,
    useLogMeIn
} from "@/components/confluence/hooks/use-log-me-in";
import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import StickyContainer from "@/patterns/sticky-container/sticky-container";
import { ConfluenceAboveNavButterbar } from "@/templates/software/confluence/confluence-software-page/src/sections/above-nav-butterbar";
import { ConfluencePageBanner } from "@/templates/software/confluence/confluence-software-page/src/sections/banner";
import { withFeatureExposed } from "@/utils/feature-exposed";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import { ConfluenceSoftwarePageProps } from "../../../../../pages/software/confluence/anchors/confluence-software-page";

import AiSection from "./sections/ai-section";
import ConfluenceSoftwareNav from "./sections/confluence-navbar";
import ContentTypesSection from "./sections/content-types-section";
import CtaSection from "./sections/cta-section";
import FeelSupportedSection from "./sections/feel-supported-section";
import HeroTextSection from "./sections/hero-text-section";
import IntegrationsSection from "./sections/integrations-section";
import HeroSection from "./sections/log-me-in/hero";
import ScrollerSection from "./sections/scroller-section";
import SocialEndorsementSection from "./sections/social-endorsement-section";
import TemplatesSection from "./sections/templates-section";
import TestimonialsSection from "./sections/testimonial-section";
import WhyConfluenceSection from "./sections/why-confluence-section";

const relativeParent = css({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden"
});

const relativeChildren = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    marginBlockStart: "6.5rem",
    gap: "6rem",

    [media.below.md]: {
        gap: "13rem",
        marginBlockStart: "2rem"
    }
});

const backgroundShape = css({
    clipPath: "polygon(0 7%, 99.25% 0, 100% 100%, 0 100%)",
    backgroundColor: wacToken("color.neutral.light100"),
    position: "absolute",
    insetBlockStart: 0,
    overflow: "hidden",
    width: 1464,
    height: 1060,

    [media.below.md]: {
        clipPath: "none",
        height: 700
    }
});

const ConfluenceSoftwarePageComponent = ({
    availableProducts,
    userProfile
}: ConfluenceSoftwarePageProps) => {
    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeIn({
        availableProducts,
        userProfile,
        siteType: AtlSiteType.CONFLUENCE
    });

    return (
        <Fragment>
            <StickyContainer>
                <ConfluenceAboveNavButterbar />
                <ConfluenceSoftwareNav />
            </StickyContainer>
            <TypographyStyles>
                <main id="content">
                    <ParentScrollTracking
                        scrollTrackingEnabled={true}
                        pageId="confluence-software-page-cfind-1234"
                    >
                        <ConfluencePageBanner />
                        {shouldShowLogMeIn ? (
                            <HeroSection
                                confluenceSites={recentSites}
                                userProfileName={userProfileName}
                            />
                        ) : (
                            <HeroTextSection userProfile={userProfile} />
                        )}
                        <ContentTypesSection />
                        <SocialEndorsementSection />
                        <ScrollerSection />
                        <TemplatesSection />
                        <WhyConfluenceSection />
                        <TestimonialsSection />
                        <div css={relativeParent}>
                            <div css={backgroundShape} />
                            <div css={relativeChildren}>
                                <IntegrationsSection />
                                <AiSection />
                            </div>
                        </div>
                        <FeelSupportedSection />
                        <CtaSection />
                    </ParentScrollTracking>
                </main>
            </TypographyStyles>
            <GlobalFooter />
        </Fragment>
    );
};

export const ConfluenceSoftwarePage = withFeatureExposed(
    "cfind-1234-product-tour-refresh-q3fy24",
    "Test",
    ConfluenceSoftwarePageComponent
);
